import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { changeUserEmail } from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';

import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { userChangeEmail } from 'core/helpers/yupSchemas';
import { useGetUser } from 'core/hooks/useGetUser';

import { SendVerifyLetterEmailModal } from 'pages/Login/SendVerifyLetterModal';

import * as Styled from './styled';

export const ChangeEmail = () => {
	const { t: signUpPageTranslate } = useTranslation(NamespacesList['signUpPage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const [updatingLoading, setUpdatingLoading] = useState(false);
	const [openSendingVerify, setIsOpenSendingVerify] = useState(false);
	const [isDisabledChangeEmailButton, setIsDisabledChangeEmailButton] = useState(true);

	const { user } = useGetUser();
	const dispatch = useAppDispatch();

	const userData = useSelector(getUser);

	const {
		handleSubmit,
		register,
		formState: { errors },
		setValue,
		getValues,
		reset,
		watch,
	} = useForm({
		resolver: yupResolver(userChangeEmail()),
	});

	const currentEmailValue = watch('email');

	const handleActionSendingVerify = () => {
		setIsOpenSendingVerify((prev) => !prev);
	};

	const handleUpdate = async (data: any) => {
		setUpdatingLoading(true);
		try {
			await dispatch(
				changeUserEmail({ redirectLink: window.location.href, userEmail: data?.email })
			).unwrap();
			!openSendingVerify && handleActionSendingVerify();
		} catch (error: any) {
			errorNotify(
				commonTranslate('errors.somethingWentWrongTitle'),
				commonTranslate('errors.somethingWentWrongDescription')
			);
			console.error(error);
		} finally {
			setUpdatingLoading(false);
		}
	};

	useEffect(() => {
		if (user.email) {
			reset();
			setValue('email', user.email);
		}
	}, [user]);

	useEffect(() => {
		setIsDisabledChangeEmailButton(
			!getValues().email || user.email === getValues().email || updatingLoading
		);
	}, [currentEmailValue, updatingLoading, getValues, user.email]);

	return (
		<form onSubmit={handleSubmit(handleUpdate)} style={{ width: '100%' }}>
			<Styled.ChangeEmailBox className="rtl-able">
				<FormControl fullWidth error={!!errors.email}>
					<Typography sx={{ fontSize: '12px' }}>{signUpPageTranslate('text.email')}</Typography>
					<TextField
						inputProps={{ style: { padding: '10px 12px' } }}
						placeholder="example@gmail.com"
						sx={{
							height: '44px',
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
							},
						}}
						{...register('email')}
					/>
					{userData.signUpError ? (
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								position: 'absolute',
								right: '12px',
								top: '100%',
								color: '#FB0B24',
							}}
						>
							{signUpPageTranslate('text.userWithThisEmailAlreadyExists')}
						</FormHelperText>
					) : (
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								position: 'absolute',
								right: '12px',
								top: '100%',
							}}
						>
							{errors.email?.message}
						</FormHelperText>
					)}
				</FormControl>
				<Box>
					<Button type="submit" disabled={isDisabledChangeEmailButton}>
						{updatingLoading ? (
							<Oval
								height={24}
								width={24}
								color="rgba(0, 0, 0, 0.26)"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="rgba(0, 0, 0, 0.26)"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							commonTranslate('buttons.changeEmail')
						)}
					</Button>
				</Box>
				<SendVerifyLetterEmailModal
					onClose={handleActionSendingVerify}
					isOpen={openSendingVerify}
					onConfirm={() => handleUpdate(getValues())}
				/>
			</Styled.ChangeEmailBox>
		</form>
	);
};
